import type { PromptConfig } from '../../config/config-types.js';
import type { RAGDocument, BusinessInfo } from '../../types/index.js';
import { toLLMMessages, type LLMMessage, type UIMessage, type StoredMessage } from '@west-tennessee-consulting/chat-types';
import { getQueryContextString } from '../rag/query-enhancer.js';
import { formatEntityDataForPrompt } from './entity-context-budget.js';
import { logger } from '../logger.js';

/**
 * Build system prompt with three-tier context and locale support
 * 
 * Tier 0: Base system prompt (locale-aware)
 * Tier 1: Entity context (what entity we're discussing)
 * Tier 2: Conversation summary (preserved intent)
 * Tier 3: Business info + knowledge base (domain context)
 */
export function buildSystemPrompt(
  config: PromptConfig,
  userContext: any,
  businessInfo: BusinessInfo | null,
  knowledgeBaseDocs: RAGDocument[],
  conversationSummary?: string,
  locale: string = 'en',
  entityContext?: { entityType: string; entityId: string; entityDisplayName: string; entityData?: Record<string, unknown>; focusedSystemPrompt?: string }
): string {
  // Use custom prompt builder if provided
  if (config.buildSystemPrompt) {
    return config.buildSystemPrompt(userContext, businessInfo, knowledgeBaseDocs, locale);
  }

  // Get locale-specific system prompt
  let prompt = getLocalizedSystemPrompt(config, locale);

  // TIER 1: Add entity context if available
  // This tells the AI what specific entity (contact, company, etc.) we're discussing
  if (entityContext) {
    const entityLabel = locale === 'es' ? 'Contexto de Entidad:' : 'Entity Context:';
    const typeLabel = locale === 'es' ? 'Tipo' : 'Type';
    const nameLabel = locale === 'es' ? 'Nombre' : 'Name';
    const detailsLabel = locale === 'es' ? 'Detalles Completos:' : 'Full Details:';
    
    // Format entity data for display — budget-capped (per-field + per-entity),
    // preserving the original snake_case → Title Case / array-join / object-
    // stringify formatting. See entity-context-budget.ts.
    const entityDataResult = formatEntityDataForPrompt(entityContext.entityData || {});

    if (entityDataResult.truncatedFieldCount > 0 || entityDataResult.omittedFieldCount > 0) {
      // Once per request (buildSystemPrompt runs once per turn) — this is the
      // measurement that the caps are actually doing work.
      logger.info('entity context capped in system prompt', {
        op: 'entity_context_budget',
        entityType: entityContext.entityType,
        truncatedFieldCount: entityDataResult.truncatedFieldCount,
        omittedFieldCount: entityDataResult.omittedFieldCount,
        charsSaved: entityDataResult.charsSaved,
      });
    }

    prompt += `\n\n═══ ENTITY CONTEXT ═══\n${entityLabel}\n- ${typeLabel}: ${entityContext.entityType}\n- ${nameLabel}: ${entityContext.entityDisplayName}\n\n${detailsLabel}\n${entityDataResult.text}\n═══════════════════════════`;

    // Entity-focused guidance (scope, response style, suggested actions). The
    // host app (Hub) composes this from its codegen-aware registry and passes
    // it through the entity context. Appended after the full-detail dump above.
    if (entityContext.focusedSystemPrompt) {
      prompt += `\n\n${entityContext.focusedSystemPrompt}`;
    }
  }

  // TIER 2: Add conversation summary if available
  // This preserves intent and context from earlier in the conversation
  if (conversationSummary) {
    const summaryLabel = locale === 'es' ? 'Resumen de Conversación:' : 'Conversation Summary:';
    prompt += `\n\n═══ CONVERSATION CONTEXT ═══\n${summaryLabel}\n${conversationSummary}\n═══════════════════════════`;
  }

  // Add user context if available
  if (userContext) {
    const contextLabel = locale === 'es' ? 'Contexto del Usuario:' : 'User Context:';
    prompt += `\n\n═══ USER CONTEXT ═══\n${contextLabel}`;
    if (userContext.name) {
      const nameLabel = locale === 'es' ? 'Nombre' : 'Name';
      prompt += `\n- ${nameLabel}: ${userContext.name}`;
    }
    if (userContext.companyName) {
      const companyLabel = locale === 'es' ? 'Empresa' : 'Company';
      prompt += `\n- ${companyLabel}: ${userContext.companyName}`;
    }
    prompt += '\n═══════════════════════════';
  }

  // Add business info if available
  if (businessInfo) {
    const businessLabel = locale === 'es' ? 'Información Empresarial:' : 'Business Information:';
    prompt += `\n\n═══ BUSINESS CONTEXT ═══\n${businessLabel}`;
    Object.entries(businessInfo).forEach(([key, value]) => {
      if (value) {
        const translatedKey = translateBusinessInfoKey(key, locale);
        prompt += `\n- ${translatedKey}: ${value}`;
      }
    });
    prompt += '\n═══════════════════════════';
  }

  // TIER 3: Add knowledge base context if available
  if (knowledgeBaseDocs.length > 0) {
    const kbLabel = locale === 'es' ? 'Base de Conocimiento Relevante:' : 'Relevant Knowledge Base:';
    const kbInstruction = locale === 'es'
      ? '\n⚠️ IMPORTANTE: Si el contenido de la base de conocimiento contiene URLs o enlaces, SIEMPRE formatea como enlaces markdown clickeables: [Texto](https://url). Por ejemplo, si ves "westtn.consulting/contact", conviértelo a "[Contacta con West Tennessee Consulting](https://westtn.consulting/contact)". NUNCA dejes URLs como texto plano.'
      : '\n⚠️ IMPORTANT: If knowledge base content contains URLs or links, ALWAYS format as clickable markdown links: [Text](https://url). For example, if you see "westtn.consulting/contact", convert it to "[Contact West Tennessee Consulting](https://westtn.consulting/contact)". NEVER leave URLs as plain text.';
    prompt += `\n\n═══ KNOWLEDGE BASE ═══\n${kbLabel}${kbInstruction}`;
    knowledgeBaseDocs.forEach((doc, index) => {
      const source = doc.source || 'Expert Knowledge Base';
      prompt += `\n\n[${index + 1}] ${source}:\n${doc.content}`;
    });
    prompt += '\n═══════════════════════════';
  }

  // Add Meridian's grounding reminder
  const expertContextLabel = locale === 'es'
    ? '\n\nContexto de Experto:'
    : '\n\nExpert Context:';
  const expertContext = locale === 'es'
    ? 'Fundamenta cada recomendación en el contexto anterior. Sé específico y accionable, e indica claramente cuando falte información en lugar de suponerla.'
    : 'Ground every recommendation in the context above. Be specific and actionable, and say plainly when information is missing rather than assuming it.';
  prompt += `${expertContextLabel}\n${expertContext}`;

  // Add language instruction
  const languageInstruction = getLanguageInstruction(locale);
  prompt += `\n\n${languageInstruction}`;

  return prompt;
}

/**
 * Get locale-specific system prompt
 */
function getLocalizedSystemPrompt(config: PromptConfig, locale: string): string {
  if (locale === 'es' && config.systemPromptEs) {
    return config.systemPromptEs;
  }
  
  return config.systemPrompt || `You are Meridian, an expert AI assistant for West Tennessee Consulting.

Your goal is to help users with their business questions, providing actionable advice based on the knowledge base and their specific business context.`;
}

/**
 * Get language-specific instructions for the LLM
 */
function getLanguageInstruction(locale: string): string {
  if (locale === 'es') {
    return `## Instrucciones de Idioma y Tono
- Responde SIEMPRE en español
- Usa terminología empresarial en español
- Mantén el tono de Meridian: profesional, autoritario pero accesible, específico y accionable
- Proporciona recomendaciones respaldadas por datos con números y métricas específicas
- Enfatiza estrategias probadas y resultados del mundo real
- Reconoce la relación del usuario con West Tennessee Consulting
- Incluye pasos claros que el usuario puede implementar inmediatamente
- Celebra el progreso y motiva la acción continua`;
  }
  
  return `## Language and Tone Instructions
- Always respond in English
- Use professional business terminology
- Maintain Meridian's tone: professional, authoritative yet approachable, specific and actionable
- Provide data-backed recommendations with specific numbers and metrics
- Emphasize proven strategies and real-world results
- Acknowledge the user's West Tennessee Consulting relationship
- Include clear steps the user can implement immediately
- Celebrate progress and motivate continued action`;
}

/**
 * Translate business info keys to the specified locale
 */
function translateBusinessInfoKey(key: string, locale: string): string {
  const translations: Record<string, Record<string, string>> = {
    es: {
      'industry': 'Industria',
      'revenue': 'Ingresos',
      'employees': 'Empleados',
      'founded': 'Fundada',
      'location': 'Ubicación',
      'businessType': 'Tipo de Negocio',
      'companyName': 'Nombre de la Empresa',
      'yearsInBusiness': 'Años en el Negocio',
      'creditScore': 'Puntuación de Crédito',
      'businessStructure': 'Estructura Empresarial',
    },
    en: {
      'industry': 'Industry',
      'revenue': 'Revenue',
      'employees': 'Employees',
      'founded': 'Founded',
      'location': 'Location',
      'businessType': 'Business Type',
      'companyName': 'Company Name',
      'yearsInBusiness': 'Years in Business',
      'creditScore': 'Credit Score',
      'businessStructure': 'Business Structure',
    }
  };
  
  return translations[locale]?.[key] || key;
}

/**
 * Format messages for AI model (LLM-only format)
 * Converts UIMessages (with parts) to LLMMessages (with content)
 * 
 * IMPORTANT: This function expects UIMessage or StoredMessage format (with parts array).
 * Do NOT pass LLMMessage[] - they are already in the correct format!
 * Passing LLMMessage[] will result in content loss because toLLMMessages()
 * tries to extract text from the parts array, which LLMMessages don't have.
 */
export function formatMessagesForAI(messages: (UIMessage | StoredMessage)[]): LLMMessage[] {
  return toLLMMessages(messages);
}

